import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  inject,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocalizationService } from '@abp/ng.core';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { BaseListConfig } from './models/base-list-config.model';
import { BaseListColumn } from './models/base-list-column.model';
import { BaseListLazyLoadEvent } from './models/base-list-event.model';
import { DisplayDateTimePipe } from 'src/core/pipes/display-date-time.pipe';

@Component({
  selector: 'app-base-list',
  standalone: true,
  imports: [CommonModule, TableModule, TooltipModule, DisplayDateTimePipe],
  templateUrl: './base-list.component.html',
  styleUrls: ['./base-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class BaseListComponent<T> {
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);

  private _config!: BaseListConfig<T>;

  @Input({ required: true })
  set config(value: BaseListConfig<T>) {
    this._config = value;
    this.cdr.markForCheck();
  }
  get config(): BaseListConfig<T> {
    return this._config;
  }

  @Input() data: T[] = [];
  @Input() totalRecords = 0;
  @Input() loading = false;
  @Input() rows = 10;
  @Input() paginator = true;
  @Input() first = 0;

  @Output() lazyLoad = new EventEmitter<BaseListLazyLoadEvent>();
  @Output() rowSelect = new EventEmitter<T>();
  @Output() rowExpand = new EventEmitter<T>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ContentChild('rowExpandTemplate') rowExpandTemplate?: TemplateRef<any>;

  /** Passed to p-table [expandedRowKeys] — PrimeNG 20 manages toggle state internally via pRowToggler. */
  expandedRowKeys: { [s: string]: boolean } = {};

  private lastLazyEvent: BaseListLazyLoadEvent | null = null;

  get columns(): BaseListColumn<T>[] {
    return this.config?.columns ?? [];
  }

  get rowsPerPageOptions(): number[] {
    return this.config?.rowsPerPageOptions ?? [10, 25, 50];
  }

  get showGridlines(): boolean {
    return this.config?.showGridlines ?? true;
  }

  get stripedRows(): boolean {
    return this.config?.stripedRows ?? true;
  }

  get rowHover(): boolean {
    return this.config?.rowHover ?? true;
  }

  get emptyMessage(): string {
    return this.config?.emptyMessage ?? this.localization.instant('::NoRecordsFound');
  }

  get isExpandable(): boolean {
    return !!(this.config?.expandable && this.rowExpandTemplate);
  }

  get defaultSortField(): string | undefined {
    return this.config?.defaultSortField;
  }

  get defaultSortOrder(): number {
    return this.config?.defaultSortOrder ?? 1;
  }

  get refreshLabel(): string {
    const val = this.localization.instant('AbpUi::Refresh');
    return val !== 'AbpUi::Refresh' ? val : 'Refresh';
  }

  get dataKey(): string {
    return (this.config?.trackByField as string) || 'id';
  }

  trackByColumn = (_index: number, column: BaseListColumn<T>): string => {
    return column.field;
  };

  trackByRow = (_index: number, item: T): unknown => {
    if (this.config?.trackByField && item) {
      const field = this.config.trackByField as keyof T;
      return item[field];
    }
    return _index;
  };

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first = event.first ?? 0;
    const rows = event.rows ?? this.rows;
    const sortField = this.resolveSortField(event.sortField);
    const sortOrder = this.resolveSortOrder(event.sortOrder);

    const lazyEvent: BaseListLazyLoadEvent = {
      skipCount: this.first,
      maxResultCount: rows,
      sortField: sortField ?? undefined,
      sortOrder,
    };

    this.lastLazyEvent = lazyEvent;
    this.lazyLoad.emit(lazyEvent);
  }

  refresh(): void {
    if (this.lastLazyEvent) {
      this.lazyLoad.emit({ ...this.lastLazyEvent });
    }
  }

  onRowClick(row: T): void {
    this.rowSelect.emit(row);
  }

  onRowExpandEvent(event: { data: T }): void {
    this.rowExpand.emit(event.data);
  }

  collapseAll(): void {
    this.expandedRowKeys = {};
    this.cdr.markForCheck();
  }

  getCellValue(row: T, column: BaseListColumn<T>): unknown {
    return row[column.field];
  }

  getRowClass(row: T): string {
    return this.config?.rowClassFn?.(row) ?? '';
  }

  private resolveSortField(sortField: TableLazyLoadEvent['sortField']): string | undefined {
    const normalized = Array.isArray(sortField) ? sortField[0] : sortField;
    return typeof normalized === 'string' && normalized.length > 0 ? normalized : undefined;
  }

  private resolveSortOrder(sortOrder: TableLazyLoadEvent['sortOrder']): 'asc' | 'desc' | undefined {
    if (sortOrder === 1) return 'asc';
    if (sortOrder === -1) return 'desc';
    return undefined;
  }
}
